import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { HiTrash } from 'react-icons/hi'
import { useApp } from '../../context/AppContext'
import FormError from '../feedback/FormError'
import adminServices from '../../services/AdminServices'
import PropertyGallery from './PropertyGallery'

function PropertyModal({ action, activeProperty, onReload, setActiveProperty }) {
    const { categories, amenities } = useApp()
    const [title, setTitle] = useState('')
    const [shortDesc, setShortDesc] = useState('')
    const [category, setCategory] = useState('')
    const [selectedAmenities, setSelectedAmenities] = useState([])
    const [nightlyRate, setNightlyRate] = useState('')
    const [weeklyRate, setWeeklyRate] = useState('')
    const [monthlyRate, setMonthlyRate] = useState('')
    const [currency, setCurrency] = useState('KES')
    const [bedrooms, setBedrooms] = useState(1)
    const [bathrooms, setBathrooms] = useState(1)
    const [status, setStatus] = useState('available')
    const [isFeatured, setIsFeatured] = useState(false)
    const [coverImage, setCoverImage] = useState(null)
    const [coverPreview, setCoverPreview] = useState('')
    const [area, setArea] = useState('')
    const [squareM, setSquareM] = useState(0)
    const [loading, setLoading] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState(0)
    const [checkInTime, setCheckInTime] = useState('12:00')
    const [checkOutTime, setCheckOutTime] = useState('10:00')

    const tabs = useMemo(() => {
        const base = ['Details', 'Pricing & Settings']
        if (action === 1 || action === 2) base.push('Gallery', 'Delete')
        return base
    }, [action])

    useEffect(() => {
        if ((action === 1 || action === 2 || action === 3) && activeProperty) {
            setTitle(activeProperty.title || '')
            setShortDesc(activeProperty.description || '')
            setCategory(activeProperty.category?.id || activeProperty.category || '')
            setSelectedAmenities(activeProperty.amenities?.map(a => a.id) || [])
            setNightlyRate(activeProperty.nightly_rate || '')
            setWeeklyRate(activeProperty.weekly_rate || '')
            setMonthlyRate(activeProperty.monthly_rate || '')
            setCurrency(activeProperty.currency || 'KES')
            setBedrooms(activeProperty.bedrooms || 1)
            setBathrooms(activeProperty.bathrooms || 1)
            setStatus(activeProperty.status || 'available')
            setIsFeatured(activeProperty.is_featured || false)
            setCoverPreview(activeProperty.cover_image || '')
            setArea(activeProperty.area || '')
            setSquareM(activeProperty.size_sqm || 0)
            setCheckInTime(activeProperty.check_in_time ? activeProperty.check_in_time.slice(0, 5) : '12:00')
            setCheckOutTime(activeProperty.check_out_time ? activeProperty.check_out_time.slice(0, 5) : '10:00')
        } else { resetForm() }
        setActiveTab(0)
    }, [action, activeProperty])

    const resetForm = () => {
        setTitle(''); setShortDesc(''); setCategory(''); setSelectedAmenities([])
        setNightlyRate(''); setWeeklyRate(''); setMonthlyRate(''); setCurrency('KES')
        setBedrooms(1); setBathrooms(1); setStatus('available'); setIsFeatured(false)
        setCoverImage(null); setCoverPreview(''); setError(null); setArea(''); setSquareM(0);
        setCheckInTime('12:00'); setCheckOutTime('10:00')
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (file) { setCoverImage(file); setCoverPreview(URL.createObjectURL(file)) }
    }

    const toggleAmenity = (id) => setSelectedAmenities(prev =>
        prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    )

    const closeModal = () => {
        document.getElementById('property-modal-close-btn')?.click()
        setActiveProperty(null)
        resetForm()
    }

    const handleSubmit = async (e) => {
        e.preventDefault(); setError(null); setLoading(true)

        try {
            let payload
            if (coverImage) {
                payload = new FormData()
                payload.append('title', title)
                payload.append('description', shortDesc)
                payload.append('category', category)
                payload.append('nightly_rate', nightlyRate)
                if (weeklyRate) payload.append('weekly_rate', weeklyRate)
                if (monthlyRate) payload.append('monthly_rate', monthlyRate)
                payload.append('currency', currency)
                payload.append('bedrooms', bedrooms)
                payload.append('bathrooms', bathrooms)
                payload.append('status', status)
                payload.append('is_featured', isFeatured)
                payload.append('cover_image', coverImage)
                payload.append('area', area)
                payload.append('size_sqm', squareM)
                payload.append('check_in_time', checkInTime)
                payload.append('check_out_time', checkOutTime)
                selectedAmenities.forEach(id => payload.append('amenities', id))
            } else {
                payload = {
                    title, description: shortDesc, category,
                    nightly_rate: nightlyRate,
                    weekly_rate: weeklyRate || null, monthly_rate: monthlyRate || null,
                    currency, bedrooms, bathrooms, status, is_featured: isFeatured,
                    amenities: selectedAmenities, area, size_sqm: squareM, check_in_time: checkInTime,
                    check_out_time: checkOutTime
                }
            }

            if (action === 1) await adminServices.adminUpdateProperty(activeProperty.id, payload)
            else await adminServices.adminCreateProperty(payload)

            toast.success(action === 1 ? 'Property updated' : 'Property created')
            onReload?.(); closeModal()
        } catch (err) {
            const msg = err.response?.data?.title?.[0] || err.response?.data?.non_field_errors?.[0] || 'Operation failed'
            setError(msg); toast.error(msg)
        } finally { setLoading(false) }
    }

    const handleDelete = async () => {
        if (!activeProperty?.id) return
        setDeleteLoading(true)
        try {
            await adminServices.adminDeleteProperty(activeProperty.id)
            toast.success('Property deleted'); onReload?.(); closeModal()
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Delete failed')
        } finally { setDeleteLoading(false) }
    }

    return (
        <div>
            <label htmlFor="property-modal" id="property-open-btn" className="hidden">Property Modal</label>
            <input type="checkbox" id="property-modal" className="modal-toggle" onChange={resetForm} />

            <div className="modal modal-bottom sm:modal-middle">
                <div className="modal-box w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <label
                        htmlFor="property-modal"
                        id="property-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                        onClick={() => {
                            closeModal()
                            setActiveProperty(null)
                        }}>✕</label>

                    <h3 className="text-lg font-bold mb-4">
                        {action === 0 && 'Add Property'}
                        {action === 1 && `Edit: ${activeProperty?.title}`}
                        {action === 2 && `Delete: ${activeProperty?.title}`}
                    </h3>

                    {tabs.length > 1 && (
                        <div className="flex justify-center mb-6 sticky top-0 bg-base-100 z-10 pt-2 pb-2 border-b border-outline-variant/10">
                            <div className="tabs tabs-boxed">
                                {tabs.map((tab, idx) => (
                                    <button key={idx} className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white' : ''}`} onClick={() => setActiveTab(idx)}>{tab}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && <FormError error={error} />}

                    {activeTab === 0 && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control md:col-span-2">
                                    <label className="label"><span className="label-text font-semibold">Title *</span></label>
                                    <input type="text" value={title} onChange={e => setTitle(e.target.value)} required className="input input-bordered w-full focus:border-primary" placeholder="e.g. Lakeside Executive Suite" />
                                </div>
                                <div className="form-control md:col-span-2">
                                    <label className="label"><span className="label-text font-semibold">Area *</span></label>
                                    <input type="text" value={area} onChange={e => setArea(e.target.value)} required className="input input-bordered w-full focus:border-primary" placeholder="e.g. Milimani" />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Category *</span></label>
                                    <select value={category} onChange={e => setCategory(e.target.value)} required className="select select-bordered w-full focus:border-primary">
                                        <option value="">Select Category</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Cover Image</span></label>
                                    <div className="flex items-center gap-3">
                                        {coverPreview && <img src={coverPreview} alt="Preview" className="w-14 h-14 rounded-lg object-cover border" />}
                                        <input type="file" accept="image/*" onChange={handleImageChange} className="file-input file-input-bordered file-input-primary w-full" />
                                    </div>
                                </div>
                                <div className="form-control md:col-span-2">
                                    <label className="label"><span className="label-text font-semibold">Description *</span></label>
                                    <textarea value={shortDesc} onChange={e => setShortDesc(e.target.value)} className="textarea textarea-bordered w-full min-h-[60px] focus:border-primary" placeholder="Brief teaser for cards..." />
                                </div>
                                <div className="form-control md:col-span-2">
                                    <label className="label mb-2"><span className="label-text font-semibold">Amenities</span></label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto p-2 border border-outline-variant rounded-lg">
                                        {amenities.map(a => (
                                            <label key={a.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-surface-container rounded transition-colors">
                                                <input type="checkbox" checked={selectedAmenities.includes(a.id)} onChange={() => toggleAmenity(a.id)} className="checkbox checkbox-secondary checkbox-sm" />
                                                <span className="text-sm">{a.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="form-control">
                                    <label className="label cursor-pointer justify-start gap-3">
                                        <input type="checkbox" checked={isFeatured} onChange={e => setIsFeatured(e.target.checked)} className="checkbox checkbox-secondary checkbox-sm" />
                                        <span className="label-text font-semibold">Featured on Homepage</span>
                                    </label>
                                </div>
                            </div>
                            <div className="modal-action">
                                <button type="submit" disabled={loading || !title.trim() || !category} className="btn btn-primary rounded-xl w-full">
                                    {loading ? <span className="loading loading-spinner loading-sm"></span> : action === 1 ? 'Update Property' : 'Create Property'}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 1 && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Nightly Rate *</span></label>
                                    <input type="number" step="0.01" value={nightlyRate} onChange={e => setNightlyRate(e.target.value)} required className="input input-bordered w-full focus:border-primary" placeholder="0.00" />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Currency</span></label>
                                    <select value={currency} onChange={e => setCurrency(e.target.value)} className="select select-bordered w-full focus:border-primary">
                                        <option value="KES">KES</option>
                                        <option value="USD">USD</option>
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Weekly Rate (Optional)</span></label>
                                    <input type="number" step="0.01" value={weeklyRate} onChange={e => setWeeklyRate(e.target.value)} className="input input-bordered w-full focus:border-primary" placeholder="Leave empty if N/A" />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Monthly Rate (Optional)</span></label>
                                    <input type="number" step="0.01" value={monthlyRate} onChange={e => setMonthlyRate(e.target.value)} className="input input-bordered w-full focus:border-primary" placeholder="Leave empty if N/A" />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Bedrooms</span></label>
                                    <input type="number" value={bedrooms} onChange={e => setBedrooms(e.target.value)} min="0" className="input input-bordered w-full focus:border-primary" />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Bathrooms</span></label>
                                    <input type="number" value={bathrooms} onChange={e => setBathrooms(e.target.value)} min="0" className="input input-bordered w-full focus:border-primary" />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Square Feet *</span></label>
                                    <input type="number" step="0.01" value={squareM} onChange={e => setSquareM(e.target.value)} min="0" className="input input-bordered w-full focus:border-primary" />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Status</span></label>
                                    <select value={status} onChange={e => setStatus(e.target.value)} className="select select-bordered w-full focus:border-primary">
                                        <option value="available">Available</option>
                                        <option value="booked">Booked</option>
                                        <option value="maintenance">Under Maintenance</option>
                                        <option value="removed">Removed</option>
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Check-in Time</span></label>
                                    <input
                                        type="time"
                                        value={checkInTime}
                                        onChange={e => setCheckInTime(e.target.value)}
                                        className="input input-bordered w-full focus:border-primary"
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text font-semibold">Check-out Time</span></label>
                                    <input
                                        type="time"
                                        value={checkOutTime}
                                        onChange={e => setCheckOutTime(e.target.value)}
                                        className="input input-bordered w-full focus:border-primary"
                                    />
                                </div>
                            </div>
                            <div className="modal-action">
                                <button type="submit" disabled={loading} className="btn btn-primary rounded-xl w-full">
                                    {loading ? <span className="loading loading-spinner loading-sm"></span> : 'Save Settings'}
                                </button>
                            </div>
                        </form>
                    )}
                    {activeTab === 2 && action !== 0 && (
                        <PropertyGallery
                            propertyId={activeProperty?.id}
                            existingGallery={activeProperty?.gallery_urls}
                            key={activeProperty?.id}
                            onImagesUpdate={() => {
                                onReload?.()
                                closeModal()
                            }}
                        />
                    )}
                    {activeTab === 3 && action !== 0 && (
                        <div className="bg-error/5 border border-error/20 rounded-xl p-5">
                            <div className="flex items-start gap-3">
                                <HiTrash className="text-error h-7 w-7 mt-1" />
                                <div>
                                    <h4 className="font-bold text-error">Delete Property</h4>
                                    <p className="text-sm text-on-surface-variant/80 mt-1">
                                        This will permanently remove <span className="font-semibold">{activeProperty?.title}</span>. All related images and bookings will be affected.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex gap-3">
                                <button onClick={() => setActiveTab(0)} className="btn btn-ghost btn-sm" disabled={deleteLoading}>Cancel</button>
                                <button onClick={handleDelete} disabled={deleteLoading} className="btn btn-error btn-sm gap-2">
                                    {deleteLoading ? <span className="loading loading-spinner loading-xs"></span> : <><HiTrash className="h-4 w-4" /> Confirm Delete</>}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
export default PropertyModal